import React, { useEffect, useRef, useState } from "react";
import { IEventSummary, IScore, IShow } from "@eagerdog/interfaces";
import { buildShowAwardsPayload, ShowAwardsForm } from "@eagerdog/scoring-core";
import { AxiosError } from "axios";

import { apiService } from "src/services/api.service";
import { toast } from "src/components/Toast/ToastManager";

import { AwardDropdown, SaveNote } from "./awardFields";
import { preferScores } from "./dogLabel";
import { NestedSection } from "./NestedSection";
import { readStreamShow } from "./storedResults";

import styles from "./UKCConformationResults.module.scss";

export interface IShowStream {
  id: "regular" | "altered" | "novice_puppy" | "veteran" | "brace";
  title: string;
  scores: IScore[];
  preferred: string[];
}

interface IProps {
  streams: IShowStream[];
  event: IEventSummary;
  show: IShow;
  onSaved: () => void;
}

type SaveStatus = "idle" | "saving" | "saved" | "error";
type ShowForm = ShowAwardsForm;

function readForm(show: IShow): ShowForm {
  return {
    regular: readStreamShow(show, "regular"),
    altered: readStreamShow(show, "altered"),
    novice_puppy: readStreamShow(show, "novice_puppy"),
    veteran: readStreamShow(show, "veteran"),
    brace: readStreamShow(show, "brace"),
  };
}

export const BestInShowPanel: React.FC<IProps> = ({ streams, event, show, onSaved }) => {
  const visible = streams.filter((stream) => stream.scores.length > 0);
  const initial = readForm(show);
  const [form, setForm] = useState<ShowForm>(initial);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const formRef = useRef(initial);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedStatusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (saveTimerRef.current) return;
    const next = readForm(show);
    setForm(next);
    formRef.current = next;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  if (!visible.length) return null;

  const triggerAutoSave = () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (savedStatusTimerRef.current) clearTimeout(savedStatusTimerRef.current);
    setSaveStatus("idle");
    saveTimerRef.current = setTimeout(async () => {
      setSaveStatus("saving");
      const payload = buildShowAwardsPayload(show, formRef.current);
      try {
        await apiService.saveConformationShowResults(event._id, show._id, payload);
        setSaveStatus("saved");
        onSaved();
        savedStatusTimerRef.current = setTimeout(() => setSaveStatus("idle"), 2000);
      } catch (error) {
        setSaveStatus("error");
        toast.show({
          title: "Error",
          content: "Failed to save Best in Show results.",
          type: "fail",
          errorDetails: error as AxiosError,
        });
      } finally {
        saveTimerRef.current = null;
      }
    }, 800);
  };

  const change = (id: IShowStream["id"], field: "best_in_show" | "reserve_best_in_show", value: string) => {
    setForm((prev) => {
      const next = { ...prev, [id]: { ...prev[id], [field]: value } };
      formRef.current = next;
      return next;
    });
    triggerAutoSave();
  };

  return (
    <NestedSection title="Best in Show" defaultOpen tone="show">
      {visible.map((stream) => {
        const scores = preferScores(stream.scores, stream.preferred);
        if (stream.id === "brace") {
          return (
            <div key={stream.id}>
              <div className={styles.sectionDivider}>{stream.title}</div>
              <div className={styles.awardGrid}>
                <AwardDropdown
                  label="Best Brace in Show"
                  scores={scores}
                  value={form.brace.best_in_show}
                  onChange={(id) => change("brace", "best_in_show", id)}
                  breed
                />
              </div>
            </div>
          );
        }
        return (
          <div key={stream.id}>
            <div className={styles.sectionDivider}>{stream.title}</div>
            <div className={styles.awardGrid}>
              <AwardDropdown
                label="Best in Show (BIS)"
                scores={scores}
                value={form[stream.id].best_in_show}
                onChange={(id) => change(stream.id, "best_in_show", id)}
                breed
              />
              <AwardDropdown
                label="Reserve Best in Show (RBIS)"
                scores={scores}
                value={form[stream.id].reserve_best_in_show}
                onChange={(id) => change(stream.id, "reserve_best_in_show", id)}
                breed
              />
            </div>
          </div>
        );
      })}
      <SaveNote status={saveStatus} />
    </NestedSection>
  );
};
